import React from 'react';
import * as Yup from 'yup';
import { firestore } from '../firebase';
import * as RFIcon from 'react-feather'; 
import language from '../languages';
import buildQuery from "./EntityUtil";

const meta = (lang) => {
    return {
        entityName: 'Restaurants',
        entityTitle: 'Restaurants',
        headerIcon: <RFIcon.Clipboard strokeWidth={2} />,
        emptyRow: () => {
            return {
                name: '',
                description: '',
                address: '',
                region: '',
                postalCode: '',
                cuisineType: '',
                priceRange: '',
                telephone: '',
                site: '',
                image: 'https://via.placeholder.com/800',
                isActive: true,
                isFeatured: false,
                featuredOrder: 0,
                mapEmbedSrc: '',
            }
        },
        collection: 'restaurants',
        addButtonText: 'New Restaurant',
        fields: [
            { name: 'name', default: null, dataType: 'string', label: 'Restaurant Name', input: 'text' },
            { name: 'description', default: null, dataType: 'string', label: 'Description', input: 'text', multiline: true, rows: 3 },
            { name: 'address', default: null, dataType: 'string', label: 'Address', input: 'text', multiline: true, rows: 3 },
            { name: 'region', default: null, dataType: 'array', label: 'Region', input: 'select',
                getItems: ()=> {
                    return new Promise((resolve, reject)=> {
                        firestore.collection('regions').where('active', '==', true).get().then(snapshot=>{
                            let regions = [];
                            snapshot.forEach(doc=>{
                                const selected = false;
                                const disabled = false;
                                const data = doc.data();
                                regions.push({ selected, ...data })
                            })
                            let allRegions = JSON.parse(JSON.stringify(regions))
                            for(let i = 0; i < allRegions.length; i++ ) {
                                const mainRegion = allRegions.filter(item => item.value == allRegions[i].mainRegion)[0]
                                if(mainRegion) {
                                    const subRegion = allRegions.filter(item => item.value == allRegions[i].subRegion)[0]
                                    if(subRegion) {
                                        regions[i].label = mainRegion.label + ' -> ' + subRegion.label + ' -> ' + allRegions[i].label
                                    }else {
                                        regions[i].label = mainRegion.label + ' - ' + allRegions[i].label
                                        regions[i].disabled = true
                                    }
                                }else {
                                    regions[i].disabled = true
                                }
                            }
                            regions.sort((item1, item2) => item1.label.localeCompare(item2.label))
                            resolve(regions);
                        });
                    })
                }
            },
            { name: 'postalCode', default: null, dataType: 'string', label: 'Postal Code', input: 'text' },
            {
                name: 'cuisineType', default: null, dataType: 'string', label: 'Cuisine Type', input: 'select',
                getItems: () => {
                    return new Promise((resolve, reject)=> {
                        firestore.collection('cuisines').where('isActive', '==', true).get().then(snapshot=>{
                            let cuisines = [];
                            snapshot.forEach(doc=>{
                                const selected = false;
                                const data = doc.data();
                                cuisines.push({ selected, ...data })
                            })
                            resolve(cuisines);
                        });
                    });
                },
                required: true
            },
            {
                name: 'priceRange', default: null, dataType: 'string', label: 'Price Range', input: 'select',
                getItems: () => {
                    return new Promise((resolve, reject)=> {
                        firestore.collection('lookups').where('code', '==', 'price').get().then(snapshot=>{
                            snapshot.forEach(doc=>{
                                const row = doc.data();
                                const items = row.values.map(item=> ({label: item.label, value: item.value}));
                                resolve(items);
                            })
                        });
                    });
                },
                required: true
            },
            { name: 'telephone', default: null, dataType: 'string', label: 'Telephone', input: 'text' },
            { name: 'site', default: null, dataType: 'string', label: 'Site', input: 'text' },
             {name: 'image', default: null, dataType: 'string', label: 'Image', input: 'file'},
             { name: 'mapEmbedSrc', default: null, dataType: 'string', label: 'Map Embed Src', input: 'text' },
             {name: 'isActive', default: true, dataType: 'boolean', label: 'Activate', input: 'switch', color: 'primary'},
             {name: 'isFeatured', default: true, dataType: 'boolean', label: 'Is Featured', input: 'switch', color: 'primary'},
             {name: 'featuredOrder', default: null, dataType: 'number', label: 'Featured Order', input: 'text', type:'number', toFixed: 0},
        ],
        table: [
            {name: 'image', type: 'field', label: 'Image',
                render: (doc)=> {
                    return (
                        <img component="span" alt={doc.name} src={doc.image} style={{height:75}} />
                    )}
            },
            { name: 'name', type: 'field', label: 'Name' },
            { name: 'region', type: 'field', label: 'Region' },
            { name: 'cuisineType', type: 'field', label: 'Cuisine Type' },
            { name: 'priceRange', type: 'field', label: 'Price Range' },
            { name: 'site', type: 'field', label: 'Site' },
            { name: 'telephone', type: 'field', label: 'Telephone' },
            { name: 'actions', type: 'action', label: 'Actions', actions: ['delete', 'edit'] }
        ],
        validationSchema: Yup.object({
            name: Yup.string()
                .required(language[lang].Field_Required),
            description: Yup.string()
                .required(language[lang].Field_Required),
            postalCode: Yup.string()
                .required(language[lang].Field_Required)
                .matches(/^[0-9]{5}$/g, 'Enter valid Postal Code'),
            image: Yup.string()
                .required(language[lang].Field_Required),
            region: Yup.string()
                .required(language[lang].Field_Required),
            cuisineType: Yup.string()
                .required(language[lang].Field_Required),
            priceRange: Yup.string()
                .required(language[lang].Field_Required),
            address: Yup.string()
                .required(language[lang].Field_Required),
            telephone: Yup.string()
                .required(language[lang].Field_Required)
                .matches(/^[0-9]{10}$/g, 'Enter valid Phone Number'),
            mapEmbedSrc: Yup.string()
                // .required(language[lang].Field_Required)
                .url(/^[0-9]{10}$/g, 'Not a Valid URL'),
            featuredOrder: Yup.number()
                .typeError("That doesn't look like a quantity")
                .integer("That doesn't look like a quantity")
                .positive("A quantity number can't start with a minus")
                .required(language[lang].Field_Required),
            site: Yup.string()
                .required(language[lang].Field_Required)
                .matches(/[-a-zA-Z0-9@:%_\+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?/gi, 'Enter valid URL'),
        }),
        filterObject: [{
            label: 'Price Range',
            name: 'priceRange',
            type: 'radio',
            items: ()=> {
                return new Promise((resolve, reject)=> {
                    firestore.collection('lookups').where('code', '==', 'price').get().then(snapshot=>{
                        snapshot.forEach(doc=>{
                            const row = doc.data();
                            const items = row.values.map(item=> ({label: item.label, value: item.value, selected: false}));
                            resolve(items);
                        })
                    });
                });
            }
        }, {
            label: 'Cuisine Type',
            name: 'cuisineType',
            type: 'checkbox',
            items: ()=> {
                return new Promise((resolve, reject)=> {
                    firestore.collection('lookups').where('code', '==', 'cuisine').get().then(snapshot=>{
                        snapshot.forEach(doc=>{
                            const row = doc.data();
                            const items = row.values.map(item=> ({label: item.label, value: item.value, selected: false}));
                            resolve(items);
                        })
                    });
                });
            }
        }],
        getQuery: (paginationAction, firstRowRef, lastRowRef, filter, searchTerms, orderBy, itemsPerPage) => {
            let query = {};
            query = firestore.collection('restaurants');
            return buildQuery(query, paginationAction, firstRowRef, lastRowRef, filter, searchTerms, orderBy, itemsPerPage);
        },
        defaultOrderBy: ['_createdOn', 'desc'],
        searchableColumns: ['name'],
        beforeSave: async (user, doc) => {
            const snapshot = await firestore.collection('regions').where('value', '==', doc.region).get();
            let region = snapshot.docs[0].data()
            doc.mainRegion = region.mainRegion
            doc.subRegion = region.subRegion
            return doc
        }
    }
}

export default meta;